import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiInterface } from '../api-interface';
import { ApiService } from '../api.service';
import {
  IntencaoDeRecebimentoDTO,
  IntencaoDeRecebimentoStatusDTO,
} from '../../models/financeiro/intencao-de-recebimento.model';

/**
 * Cobrança por QR Code das vendas (Bancard QR Vendas).
 *
 * A conta de destino do dinheiro é a configurada em "Dados de recebimento"; se a empresa
 * ainda não configurou, `gerarQrCode` responde 400 com mensagem própria.
 */
@Injectable({
  providedIn: 'root',
})
export class IntencaoDeRecebimentoService implements ApiInterface {

  constructor(private apiService: ApiService) {}

  // --- Metodos padrao ApiInterface ---

  get<T>(url: string, data?: any): Observable<any> {
    const queryParams = this.apiService.buildQueryParams(data);
    const path = queryParams ? `${url}?${queryParams}` : url;
    return this.apiService.read<any>(path);
  }

  post<T>(url: string, data: any): Observable<any> {
    return this.apiService.create<any>(url, data);
  }

  put<T>(url: string, data: any): Observable<any> {
    return this.apiService.update<any>(url, data);
  }

  delete<T>(url: string): Observable<any> {
    return this.apiService.delete(url);
  }

  // --- Metodos especificos ---

  /**
   * GET /intencaoDeRecebimento/gerarQRCode — cria a cobrança e devolve o QR.
   *
   * @param valor total a cobrar, na moeda da empresa.
   * @param emissao data/hora da venda em ISO local (sem Z).
   * @param pessoaId cliente da venda, quando houver.
   */
  gerarQrCode(
    valor: number,
    emissao: string,
    pessoaId?: number | null,
    tipoReceita: string = 'VENDAS_A_VISTA',
  ): Observable<IntencaoDeRecebimentoDTO> {
    // Montado a mao porque `buildQueryParams` so aceita os parametros de paginacao/filtro
    // padrao (ApiQueryParamsDTO), e aqui os nomes sao especificos deste endpoint.
    const params = new URLSearchParams({
      valor: String(valor),
      emissao,
      tipoReceita,
    });
    if (pessoaId) {
      params.set('pessoa_id', String(pessoaId));
    }
    return this.apiService.read<IntencaoDeRecebimentoDTO>(
      `/intencaoDeRecebimento/gerarQRCode?${params.toString()}`
    );
  }

  /**
   * PATCH /intencaoDeRecebimento/{id}/cancelar — invalida uma cobranca que a tela deixou de esperar.
   *
   * O QR ja mostrado continua valido na Bancard depois que o operador fecha o dialogo ou troca a
   * forma de pagamento: sem este cancelamento, um pagamento que chegasse depois seria confirmado
   * como se o pedido ainda estivesse de pe.
   */
  cancelar(id: number): Observable<IntencaoDeRecebimentoStatusDTO> {
    return this.apiService.patch<IntencaoDeRecebimentoStatusDTO>(
      `/intencaoDeRecebimento/${id}/cancelar`,
      {}
    );
  }

  /**
   * GET /intencaoDeRecebimento/{id}/status — usado no polling enquanto o cliente paga.
   */
  consultarStatus(id: number): Observable<IntencaoDeRecebimentoStatusDTO> {
    return this.apiService.read<IntencaoDeRecebimentoStatusDTO>(
      `/intencaoDeRecebimento/${id}/status`
    );
  }
}
